import React, { useState, useEffect } from 'react'
import {
  Box,
  Container,
  Typography,
  Card,
  CardContent,
  Button,
  AppBar,
  Toolbar,
  IconButton,
  Tabs,
  Tab,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  Avatar,
  Chip,
  Divider,
  Alert
} from '@mui/material'
import {
  ArrowBack as ArrowBackIcon,
  Description as DescriptionIcon,
  Group as GroupIcon,
  Person as PersonIcon,
  Code as CodeIcon,
  School as SchoolIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  CheckCircle as CheckCircleIcon,
  Cancel as CancelIcon,
  Sort as SortIcon
} from '@mui/icons-material'
import { useAuth } from '../hooks/useAuth'
import { auth, db } from '../firebase/config'
import { collection, query, where, getDocs, doc, getDoc, updateDoc } from 'firebase/firestore'
import toast from 'react-hot-toast'
import StudentDetail from './StudentDetail'

const ClassDetail = ({ classData, onBack, userData }) => {
  const { user } = useAuth()
  const [currentTab, setCurrentTab] = useState(0)
  const [students, setStudents] = useState([])
  const [documents, setDocuments] = useState([])
  const [loading, setLoading] = useState(true)
  const [selectedStudent, setSelectedStudent] = useState(null)
  const [studentsWithScores, setStudentsWithScores] = useState([])
  const [sortBy, setSortBy] = useState('name') // 'name', 'score', 'productivity'

  useEffect(() => {
    if (user && user.uid) {
      loadClassData()
    }
  }, [user, classData.id])

  const loadClassData = async () => {
    if (!user || !user.uid) {
      console.log('User not ready yet')
      return
    }

    try {
      setLoading(true)
      
      // Öğrencileri yükle
      const studentsRef = collection(db, 'users')
      const studentsQuery = query(
        studentsRef, 
        where('role', '==', 'student'),
        where('classIds', 'array-contains', classData.id)
      )
      const studentsSnapshot = await getDocs(studentsQuery)
      
      const studentsData = studentsSnapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }))
      setStudents(studentsData)
      
      // Load student scores and evaluations
      await loadStudentScoresAndEvaluations(studentsData)

      // Dökümanları yükle - Öğrenci ve öğretmen için farklı sorgular
      const documentsRef = collection(db, 'sentDocuments')
      let documentsData = []
      
      const isTeacher = userData?.role === 'teacher'
      console.log('👤 Kullanıcı rolü:', userData?.role, 'Öğretmen mi:', isTeacher)
      
      if (isTeacher) {
        // Öğretmen için döküman yükleme
        console.log('👨‍🏫 Öğretmen döküman yükleme başlatılıyor...')
        
        // 1. Önce classId ile ara
        console.log('🔍 1. classId ile döküman arama yapılıyor:', classData.id)
        const classIdQuery = query(
          documentsRef,
          where('classId', '==', classData.id),
          where('teacherId', '==', user.uid),
          where('documentType', '==', 'class')
        )
        
        const classIdSnapshot = await getDocs(classIdQuery)
        console.log('📄 classId ile bulunan döküman sayısı:', classIdSnapshot.size)
        
        for (const doc of classIdSnapshot.docs) {
          const docData = { id: doc.id, ...doc.data() }
          console.log('📖 classId ile bulunan döküman:', docData)
          documentsData.push(docData)
        }
        
        // 2. Eğer classId ile bulunamadıysa, className ile ara
        if (documentsData.length === 0) {
          console.log('🔍 2. className ile döküman arama yapılıyor:', classData.className)
          const classNameQuery = query(
            documentsRef,
            where('className', '==', classData.className),
            where('teacherId', '==', user.uid),
            where('documentType', '==', 'class')
          )
          
          const classNameSnapshot = await getDocs(classNameQuery)
          console.log('📄 className ile bulunan döküman sayısı:', classNameSnapshot.size)
          
          for (const doc of classNameSnapshot.docs) {
            const docData = { id: doc.id, ...doc.data() }
            console.log('📖 className ile bulunan döküman:', docData)
            documentsData.push(docData)
          }
        }
        
        // 3. Eğer hala bulunamadıysa, sadece teacherId ile ara
        if (documentsData.length === 0) {
          console.log('🔍 3. Sadece teacherId ile döküman arama yapılıyor:', user.uid)
          const teacherQuery = query(
            documentsRef,
            where('teacherId', '==', user.uid),
            where('documentType', '==', 'class')
          )
          
          const teacherSnapshot = await getDocs(teacherQuery)
          console.log('📄 teacherId ile bulunan döküman sayısı:', teacherSnapshot.size)
          
          for (const doc of teacherSnapshot.docs) {
            const docData = { id: doc.id, ...doc.data() }
            console.log('📖 teacherId ile bulunan döküman:', docData)
            documentsData.push(docData)
          }
        }
      } else {
        // Öğrenci için döküman yükleme
        console.log('👨‍🎓 Öğrenci döküman yükleme başlatılıyor...')
        
        // Öğrenci için sınıfa gönderilen tüm dökümanları yükle
        console.log('🔍 Öğrenci için classId ile döküman arama yapılıyor:', classData.id)
        const studentClassQuery = query(
          documentsRef,
          where('classId', '==', classData.id),
          where('documentType', '==', 'class')
        )
        
        const studentClassSnapshot = await getDocs(studentClassQuery)
        console.log('📄 Öğrenci için bulunan döküman sayısı:', studentClassSnapshot.size)
        
        for (const doc of studentClassSnapshot.docs) {
          const docData = { id: doc.id, ...doc.data() }
          console.log('📖 Öğrenci için bulunan döküman:', docData)
          documentsData.push(docData)
        }
        
        // Eğer classId ile bulunamadıysa, className ile ara
        if (documentsData.length === 0) {
          console.log('🔍 Öğrenci için className ile döküman arama yapılıyor:', classData.className)
          const studentClassNameQuery = query(
            documentsRef,
            where('className', '==', classData.className),
            where('documentType', '==', 'class')
          )
          
          const studentClassNameSnapshot = await getDocs(studentClassNameQuery)
          console.log('📄 Öğrenci için className ile bulunan döküman sayısı:', studentClassNameSnapshot.size)
          
          for (const doc of studentClassNameSnapshot.docs) {
            const docData = { id: doc.id, ...doc.data() }
            console.log('📖 Öğrenci için className ile bulunan döküman:', docData)
            documentsData.push(docData)
          }
        }
      }
      
      // Debug: Tüm dökümanları kontrol et
      console.log('🔍 Debug: Tüm dökümanları kontrol ediliyor...')
      const allDocumentsSnapshot = await getDocs(collection(db, 'sentDocuments'))
      console.log('🌍 Firestore\'daki toplam döküman sayısı:', allDocumentsSnapshot.size)
      allDocumentsSnapshot.docs.forEach(doc => {
        const data = doc.data()
        console.log('📋 Döküman:', { 
          id: doc.id, 
          classId: data.classId,
          className: data.className,
          teacherId: data.teacherId,
          documentType: data.documentType,
          documentName: data.documentName || data.title
        })
      })
      
      console.log('📚 Yüklenen dökümanlar:', documentsData)
      setDocuments(documentsData)
    } catch (error) {
      console.error('Error loading class data:', error)
      toast.error('Sınıf verileri yüklenirken hata oluştu')
    } finally {
      setLoading(false)
    }
  }

  const handleTabChange = (event, newValue) => {
    setCurrentTab(newValue)
  }

  const handleStudentClick = (student) => {
    // Öğrenci görünümünde öğrenci detayına gidemez
    if (userData?.role === 'student') {
      toast.info('Öğrenci görünümünde öğrenci detaylarına erişemezsiniz')
      return
    }
    setSelectedStudent(student)
  }

  const handleBackToClassDetail = () => {
    setSelectedStudent(null)
  }

  const loadStudentScoresAndEvaluations = async (studentsData) => {
    try {
      const studentsWithData = []
      
      console.log('🔍 Öğrenci puanları yükleniyor...')
      console.log('👨‍🏫 Öğretmen ID:', user.uid)
      console.log('🏫 Sınıf ID:', classData.id)
      
      for (const student of studentsData) {
        const studentDoc = await getDoc(doc(db, 'users', student.id))
        if (studentDoc.exists()) {
          const data = studentDoc.data()
          const classScores = data.classScores || {}
          const classEvaluations = data.classEvaluations || {}
          
          console.log(`📊 ${student.username || student.email} - classScores:`, classScores)
          
          // Get score for this class
          let score = 0
          if (classScores[classData.id]) {
            const scoreData = classScores[classData.id]
            console.log(`📈 ${student.username || student.email} - scoreData:`, scoreData)
            
            if (userData?.role === 'teacher') {
              // Öğretmen görünümünde: Bu öğretmenin verdiği puanı göster
              if (scoreData.teacherId === user.uid) {
                score = scoreData.score || 0
                console.log(`✅ ${student.username || student.email} - Öğretmen puanı bulundu:`, score)
              } else {
                console.log(`❌ ${student.username || student.email} - Öğretmen eşleşmiyor. Beklenen: ${user.uid}, Bulunan: ${scoreData.teacherId}`)
              }
            } else {
              // Öğrenci görünümünde: Tüm öğrencilerin puanlarını göster (Android uygulamasındaki gibi)
              score = scoreData.score || 0
              console.log(`✅ ${student.username || student.email} - Öğrenci puanı bulundu:`, score)
            }
          } else {
            console.log(`❌ ${student.username || student.email} - Bu sınıfta puan yok`)
          }
          
          // Get today's productivity evaluation
          const today = new Date().toLocaleDateString('tr-TR')
          let todayProductive = null
          if (classEvaluations[classData.id]) {
            const evalData = classEvaluations[classData.id]
            
            // Eski format (object) kontrolü
            if (!Array.isArray(evalData)) {
              if (evalData.teacherId === user.uid && evalData.date === today) {
                todayProductive = evalData.productive
              }
            } else {
              // Yeni format (array) kontrolü
              const todayEval = evalData.find(
                evaluation => evaluation.date === today && evaluation.teacherId === user.uid
              )
              if (todayEval) {
                todayProductive = todayEval.productive
              }
            }
          }
          
          studentsWithData.push({
            ...student,
            score,
            todayProductive
          })
        } else {
          studentsWithData.push({
            ...student,
            score: 0,
            todayProductive: null
          })
        }
      }
      
      console.log('📋 Yüklenen öğrenci verileri:', studentsWithData)
      setStudentsWithScores(studentsWithData)
    } catch (error) {
      console.error('Error loading student scores and evaluations:', error)
      setStudentsWithScores(studentsData.map(student => ({
        ...student,
        score: 0,
        todayProductive: null
      })))
    }
  }

  const handleProductivityToggle = async (studentId, isProductive) => {
    try {
      const today = new Date().toLocaleDateString('tr-TR')
      const studentDoc = await getDoc(doc(db, 'users', studentId))
      
      if (studentDoc.exists()) {
        const data = studentDoc.data()
        const classEvaluations = data.classEvaluations || {}
        
        // Sınıf için evaluations array'i al veya oluştur
        if (!classEvaluations[classData.id]) {
          classEvaluations[classData.id] = []
        }
        
        // Bugün için mevcut bir değerlendirme var mı kontrol et
        const existingEvalIndex = classEvaluations[classData.id].findIndex(
          evaluation => evaluation.date === today && evaluation.teacherId === user.uid
        )
        
        const newEvaluation = {
          teacherId: user.uid,
          date: today,
          productive: isProductive,
          timestamp: new Date().toISOString()
        }
        
        if (existingEvalIndex >= 0) {
          // Mevcut değerlendirmeyi güncelle
          classEvaluations[classData.id][existingEvalIndex] = newEvaluation
        } else {
          // Yeni değerlendirme ekle
          classEvaluations[classData.id].push(newEvaluation)
        }
        
        await updateDoc(doc(db, 'users', studentId), {
          classEvaluations: classEvaluations
        })
        
        // Update local state
        setStudentsWithScores(prev => prev.map(student => 
          student.id === studentId 
            ? { ...student, todayProductive: isProductive }
            : student
        ))
        
        toast.success('Verimlilik değerlendirmesi güncellendi')
        console.log('📊 Verimlilik değerlendirmesi kaydedildi:', newEvaluation)
      }
    } catch (error) {
      console.error('Error updating productivity evaluation:', error)
      toast.error('Verimlilik değerlendirmesi güncellenirken hata oluştu')
    }
  }

  const getSortedStudents = () => {
    const sorted = [...studentsWithScores]
    
    switch (sortBy) {
      case 'score':
        return sorted.sort((a, b) => b.score - a.score)
      case 'productivity':
        return sorted.sort((a, b) => {
          if (a.todayProductive === b.todayProductive) return 0
          if (a.todayProductive === true) return -1
          if (b.todayProductive === true) return 1
          if (a.todayProductive === false) return -1
          if (b.todayProductive === false) return 1
          return 0
        })
      case 'name':
      default:
        return sorted.sort((a, b) => 
          (a.username || a.email).localeCompare(b.username || b.email)
        )
    }
  }

  const TabPanel = ({ children, value, index }) => (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`tabpanel-${index}`}
      aria-labelledby={`tab-${index}`}
    >
      {value === index && <Box sx={{ py: 2 }}>{children}</Box>}
    </div>
  )

  // Show StudentDetail if a student is selected
  if (selectedStudent) {
    return (
      <StudentDetail 
        studentData={selectedStudent} 
        classData={classData} 
        onBack={handleBackToClassDetail}
        userData={userData}
      />
    )
  }

  return (
    <Box sx={{ flexGrow: 1 }}>
      {/* App Bar */}
      <AppBar position="static" sx={{ 
        background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
        boxShadow: '0 4px 20px rgba(0,0,0,0.1)'
      }}>
        <Toolbar>
          <IconButton
            edge="start"
            color="inherit"
            onClick={onBack}
            sx={{ mr: 2 }}
          >
            <ArrowBackIcon />
          </IconButton>
          <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
            {classData.className}
          </Typography>
        </Toolbar>
      </AppBar>

      {/* Main Content */}
      <Container maxWidth="md" sx={{ py: 3 }}>
        {/* Class Info Card */}
        <Card sx={{ mb: 3 }}>
          <CardContent>
            <Box display="flex" alignItems="center" mb={2}>
              <SchoolIcon sx={{ mr: 1, color: 'primary.main' }} />
              <Typography variant="h5" fontWeight="bold" color="primary">
                {classData.className}
              </Typography>
            </Box>
            
            <Typography variant="body1" color="text.secondary" mb={2}>
              {classData.description || 'Açıklama yok'}
            </Typography>
            
            <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
              <Chip
                icon={<GroupIcon />}
                label={`${students.length} öğrenci`}
                color="secondary"
              />
              <Chip
                icon={<CodeIcon />}
                label={`Join: ${classData.joinCode}`}
                variant="outlined"
              />
              {classData.level && (
                <Chip
                  label={`Seviye: ${classData.level}`}
                  color="info"
                />
              )}
            </Box>
          </CardContent>
        </Card>

        {/* Tabs */}
        <Box sx={{ borderBottom: 1, borderColor: 'divider', mb: 2 }}>
          <Tabs value={currentTab} onChange={handleTabChange}>
            <Tab 
              label="Dökümanlar" 
              icon={<DescriptionIcon />}
              iconPosition="start"
            />
            <Tab 
              label="Öğrenciler" 
              icon={<GroupIcon />}
              iconPosition="start"
            />
          </Tabs>
        </Box>

        {/* Tab Content */}
        <TabPanel value={currentTab} index={0}>
          {/* Dökümanlar Tab */}
          {!user || !user.uid ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
            </Box>
          ) : loading ? (
            <Box sx={{ textAlign: 'center', py: 4 }}>
              <Typography>Dökümanlar yükleniyor...</Typography>
            </Box>
          ) : documents.length === 0 ? (
            <Card>
              <CardContent sx={{ textAlign: 'center', py: 4 }}>
                <DescriptionIcon sx={{ fontSize: 60, color: '#6E6D6D', mb: 2 }} />
                <Typography variant="h6" color="#6E6D6D" gutterBottom>
                  Dökümanlar bulunamadı
                </Typography>
                <Typography variant="body2" color="#6E6D6D" sx={{ mb: 2 }}>
                  Bu sınıfa ait dökümanlar bulunamadı.<br />
                  Lütfen konsol loglarını kontrol edin.
                </Typography>
                <Alert severity="info" sx={{ mb: 2 }}>
                  <Typography variant="body2">
                    <strong>Debug Bilgileri:</strong><br />
                    Class ID: {classData.id}<br />
                    Class Name: {classData.className}<br />
                    Teacher ID: {user?.uid}
                  </Typography>
                </Alert>
              </CardContent>
            </Card>
          ) : (
            <List>
              {documents.map((doc, index) => (
                <React.Fragment key={doc.id}>
                  <ListItem button onClick={() => window.open(doc.documentUrl, '_blank')}>
                    <ListItemAvatar>
                      <Avatar>
                        <DescriptionIcon />
                      </Avatar>
                    </ListItemAvatar>
                    <ListItemText
                      primary={doc.documentName || doc.title || 'Döküman Adı'}
                      secondary={
                        doc.sentDate ? 
                        `Gönderilme: ${new Date(doc.sentDate.toDate()).toLocaleString('tr-TR')}` :
                        doc.description || ''
                      }
                    />
                  </ListItem>
                  {index < documents.length - 1 && <Divider />}
                </React.Fragment>
              ))}
            </List>
          )}
        </TabPanel>

                 <TabPanel value={currentTab} index={1}>
           {/* Öğrenciler Tab */}
           {!user || !user.uid ? (
             <Box sx={{ textAlign: 'center', py: 4 }}>
               <Typography>Kullanıcı bilgileri yükleniyor...</Typography>
             </Box>
           ) : loading ? (
             <Box sx={{ textAlign: 'center', py: 4 }}>
               <Typography>Öğrenciler yükleniyor...</Typography>
             </Box>
           ) : studentsWithScores.length === 0 ? (
             <Card>
               <CardContent sx={{ textAlign: 'center', py: 4 }}>
                 <GroupIcon sx={{ fontSize: 60, color: '#6E6D6D', mb: 2 }} />
                 <Typography variant="h6" color="#6E6D6D" gutterBottom>
                   Henüz öğrenci yok
                 </Typography>
                 <Typography variant="body2" color="#6E6D6D">
                   Bu sınıfa henüz öğrenci katılmamış
                 </Typography>
               </CardContent>
             </Card>
           ) : (
             <>
                                               {/* Sorting Controls - Öğretmen ve öğrenci görünümünde */}
                 {(userData?.role === 'teacher' || userData?.role === 'student') && (
                  <Card sx={{ mb: 2 }}>
                    <CardContent>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 2, flexWrap: 'wrap' }}>
                        <Typography variant="subtitle2" color="text.secondary">
                          Sıralama:
                        </Typography>
                        <Button
                          size="small"
                          variant={sortBy === 'name' ? 'contained' : 'outlined'}
                          onClick={() => setSortBy('name')}
                          startIcon={<SortIcon />}
                        >
                          İsim
                        </Button>
                        <Button
                          size="small"
                          variant={sortBy === 'score' ? 'contained' : 'outlined'}
                          onClick={() => setSortBy('score')}
                          startIcon={<SortIcon />}
                        >
                          Puan
                        </Button>
                      </Box>
                    </CardContent>
                  </Card>
                )}

               <List>
                 {getSortedStudents().map((student, index) => (
                   <React.Fragment key={student.id}>
                                           <ListItem 
                        button={userData?.role === 'teacher'}
                        onClick={() => handleStudentClick(student)}
                        sx={{ 
                          display: 'flex', 
                          justifyContent: 'space-between',
                          alignItems: 'center',
                          py: 2,
                          cursor: userData?.role === 'teacher' ? 'pointer' : 'default',
                          '&:hover': userData?.role === 'teacher' ? {
                            backgroundColor: 'action.hover'
                          } : {}
                        }}
                      >
                       <Box sx={{ display: 'flex', alignItems: 'center', flexGrow: 1 }}>
                                                   <ListItemAvatar>
                            <Avatar>
                              {student.profileImageUrl ? (
                                <img 
                                  src={student.profileImageUrl} 
                                  alt={student.username}
                                  style={{
                                    width: '100%',
                                    height: '100%',
                                    objectFit: 'cover'
                                  }}
                                />
                              ) : (
                                <PersonIcon />
                              )}
                            </Avatar>
                          </ListItemAvatar>
                         <ListItemText
                           primary={student.username || student.email}
                                                       secondary={
                              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mt: 0.5 }}>
                                <Chip
                                  label={`Puan: ${student.score}`}
                                  size="small"
                                  color="warning"
                                  variant="filled"
                                />
                              </Box>
                            }
                         />
                       </Box>
                       
                                               {/* Productivity Buttons - Sadece öğretmen görünümünde */}
                        {userData?.role === 'teacher' && (
                          <Box sx={{ display: 'flex', gap: 1, ml: 2 }}>
                            <IconButton
                              size="small"
                              color={student.todayProductive === true ? 'success' : 'default'}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleProductivityToggle(student.id, true)
                              }}
                              sx={{
                                border: student.todayProductive === true ? 2 : 1,
                                borderColor: student.todayProductive === true ? 'success.main' : 'grey.300'
                              }}
                            >
                              <AddIcon />
                            </IconButton>
                            <IconButton
                              size="small"
                              color={student.todayProductive === false ? 'error' : 'default'}
                              onClick={(e) => {
                                e.stopPropagation()
                                handleProductivityToggle(student.id, false)
                              }}
                              sx={{
                                border: student.todayProductive === false ? 2 : 1,
                                borderColor: student.todayProductive === false ? 'error.main' : 'grey.300'
                              }}
                            >
                              <RemoveIcon />
                            </IconButton>
                          </Box>
                        )}
                     </ListItem>
                     {index < getSortedStudents().length - 1 && <Divider />}
                   </React.Fragment>
                 ))}
               </List>
             </>
           )}
         </TabPanel>
      </Container>
    </Box>
  )
}

export default ClassDetail
